import React, { useEffect, useRef, useState } from 'react';
import RequestReceived from '../components/RequestReceived';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sb } from '../supabase';
import { completeTelrReturn, isNotPaidError, safeBackPath } from '../lib/telrPay';
import { waLink } from '../lib/maabarContact';

// Landing page Telr redirects back to after the hosted-payment page. Verifies the
// payment server-side (telr-verify) and shows ONE of three unmistakable outcomes:
//   ok      — paid (telr-verify 200)
//   notpaid — declined / cancelled (telr-verify 402 'not in a paid state'):
//             nothing was charged, try again from where you paid
//   failed  — anything else: we could not confirm; if the card was charged,
//             contact support (the payment may well have gone through)
// "Back" returns to the page the trader paid from — the `back` param that
// startTelrPayment adds, validated by safeBackPath — falling back to the
// dashboard for return URLs minted before that param existed.
//
// stage=managed_fee is the request fee (299 SAR): on success the request is
// released to the Maabar team (the DB settle trigger sends the admin alert and
// the trader's WhatsApp). A managed request aimed at a factory emails that
// factory only now — factory-request-email refuses while the fee is unpaid.
export default function TelrReturn({ lang = 'ar' }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [params] = useSearchParams();
  const id = params.get('requestId') || params.get('invoiceId');
  const isFee = params.get('stage') === 'managed_fee';
  const factoryId = params.get('factory') || '';
  const [state, setState] = useState('verifying'); // verifying | ok | notpaid | failed
  const [message, setMessage] = useState('');

  // Where "back" goes. The request fee never returns to a request form (that
  // would invite a second request): only to the tracker or the dashboard.
  const back = safeBackPath(params.get('back'));
  const backPath = isFee
    ? (back.startsWith('/managed-order/') || back.startsWith('/dashboard') ? back : '/dashboard?tab=requests')
    : (back || '/dashboard');
  const backKind = backPath.startsWith('/managed-order/') ? 'order'
    : backPath.startsWith('/chat/') ? 'chat' : 'dashboard';

  // Verify ONCE per reference. The ref guard means React StrictMode's (dev) double
  // effect-invoke fires telr-verify only once. We deliberately do NOT gate the
  // setState on a per-run "cancelled" flag: StrictMode's synthetic cleanup would
  // flip it and swallow the result, leaving an eternal spinner even though the
  // payment succeeded. The page is transient (navigates via button), so a stray
  // setState after a real unmount is harmless. The server is idempotent regardless.
  const startedRef = useRef('');
  useEffect(() => {
    if (!id) { setState('failed'); setMessage(isAr ? 'مرجع الدفع غير معروف.' : 'Unknown payment reference.'); return; }
    if (startedRef.current === id) return;
    startedRef.current = id;
    (async () => {
      try {
        await completeTelrReturn(id);
        setState('ok');
        if (isFee && factoryId) {
          // Best-effort, like the wizard's own call it replaces: the request is
          // saved and paid whatever happens to the email.
          sb.functions.invoke('factory-request-email', { body: { requestId: id, factoryId } })
            .catch((e) => console.error('[TelrReturn] factory email after fee failed:', e?.message || e));
        }
      } catch (e) {
        if (isNotPaidError(e)) {
          setState('notpaid');
        } else {
          console.error('[TelrReturn] could not confirm the payment:', e?.status || '', e?.message || e);
          setState('failed');
          setMessage(e?.message || '');
        }
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const backLabel = {
    order: isAr ? 'العودة إلى طلبك' : 'Back to your order',
    chat: isAr ? 'العودة إلى المحادثة' : 'Back to the chat',
    dashboard: isAr ? 'العودة إلى طلباتي' : 'Back to my orders',
  }[backKind];

  const t = {
    verifying: isAr ? 'جارٍ تأكيد الدفع…' : 'Confirming your payment…',
    verifyingNote: isAr ? 'لا تغلق هذه الصفحة.' : 'Please keep this page open.',
    ok: isAr ? 'تمت عملية الدفع بنجاح' : 'Your payment went through',
    okBody: isFee
      ? (isAr ? 'استلمنا رسوم تأكيد الطلب، وطلبك الآن عند فريق مَعبر.' : 'We received your request confirmation fee — your request is now with the Maabar team.')
      : backKind === 'order'
        ? (isAr ? 'سجّلنا دفعتك. سيتولّى فريق مَعبر الخطوة التالية، وتتابعها من صفحة طلبك.' : 'Your payment is recorded. The Maabar team takes the next step — follow it on your order page.')
        : (isAr ? 'سجّلنا دفعتك وحدّثنا طلبك. ستجد الحالة الجديدة في طلبك.' : "Your payment is recorded and your order is updated. You'll see the new status on your order."),
    notPaidTitle: isAr ? 'لم تتم عملية الدفع' : 'The payment did not go through',
    notPaidBody: isAr ? 'لم يُخصم أي مبلغ. يمكنك المحاولة مرة أخرى.' : 'Nothing was charged. You can try again.',
    feeSaved: isAr ? 'طلبك محفوظ — تقدر تكمل دفع الرسوم من «طلباتي».' : 'Your request is saved — you can complete the fee from My requests.',
    tryAgain: isAr ? 'المحاولة مرة أخرى' : 'Try again',
    failedTitle: isAr ? 'تعذّر تأكيد الدفع' : "We couldn't confirm the payment",
    failedBody: isAr ? 'إذا خُصم المبلغ من بطاقتك تواصل مع دعم معبر.' : 'If your card was charged, contact Maabar support.',
    support: isAr ? 'تواصل مع دعم معبر' : 'Contact Maabar support',
  };

  const primaryBtn = { padding: '11px 22px', borderRadius: 10, border: 'none', background: 'var(--text-primary)', color: 'var(--bg-base)', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' };
  const secondaryBtn = { ...primaryBtn, border: '1px solid var(--border)', background: 'none', color: 'var(--text-primary)' };
  const supportText = isAr
    ? `مرحباً، دفعت عبر معبر ولم يتأكد الدفع. المرجع: ${id || '—'}`
    : `Hello, I paid on Maabar and the payment wasn't confirmed. Reference: ${id || '—'}`;

  return (
    <div style={{ minHeight: 'var(--app-dvh)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', padding: 24 }}>
      <div dir={isAr ? 'rtl' : 'ltr'} role="status" aria-live="polite" style={{ maxWidth: 420, width: '100%', textAlign: 'center', background: 'var(--bg-overlay)', border: '1px solid var(--border)', borderRadius: 16, padding: '40px 28px' }}>
        {state === 'verifying' && (
          <>
            <div style={{ width: 40, height: 40, margin: '0 auto 20px', border: '3px solid var(--border)', borderTopColor: 'var(--text-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 6 }}>{t.verifying}</p>
            <p style={{ color: 'var(--text-disabled)', fontSize: 12 }}>{t.verifyingNote}</p>
          </>
        )}
        {state === 'ok' && (
          <>
            <div style={{ width: 56, height: 56, margin: '0 auto 14px', borderRadius: '50%', background: '#2F5D3A', color: '#fff', fontSize: 30, lineHeight: '56px' }}>✓</div>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{t.ok}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, lineHeight: 1.8, marginBottom: isFee ? 14 : 22 }}>{t.okBody}</p>
            {/* The fee is how a managed / team-search request is submitted, so
                this IS its confirmation screen (owner, 2026-10-03). */}
            {isFee && <RequestReceived lang={lang} style={{ marginBottom: 22, textAlign: 'start' }} />}
            <button onClick={() => nav(backPath)} style={primaryBtn}>{backLabel}</button>
          </>
        )}
        {state === 'notpaid' && (
          <>
            <div style={{ width: 56, height: 56, margin: '0 auto 14px', borderRadius: '50%', background: '#8A3B2E', color: '#fff', fontSize: 30, lineHeight: '56px' }}>✕</div>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{t.notPaidTitle}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, lineHeight: 1.8, marginBottom: isFee ? 8 : 22 }}>{t.notPaidBody}</p>
            {isFee && <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 18 }}>{t.feeSaved}</p>}
            <button onClick={() => nav(backPath)} style={primaryBtn}>{t.tryAgain}</button>
          </>
        )}
        {state === 'failed' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{t.failedTitle}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, lineHeight: 1.8, marginBottom: 10 }}>{t.failedBody}</p>
            {message && <p dir="ltr" style={{ color: 'var(--text-disabled)', fontSize: 11.5, marginBottom: 18 }}>{message}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: message ? 0 : 12 }}>
              <a href={waLink(supportText)} target="_blank" rel="noopener noreferrer" style={primaryBtn}>{t.support}</a>
              <button onClick={() => nav(backPath)} style={secondaryBtn}>{backLabel}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
